import _ from 'lodash';
import { stringify } from 'qs';
import React, { useEffect } from 'react';
import { Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { ADMINISTRATION_ROUTE_PATHS } from '@/administration/administration.constants';
import { agentBuilderAvailable } from '@/agentBuilder/agentBuilder.utilities';
import { Builder } from '@/builder/Builder.molecule';
import { shouldOfferCapturesManagement } from '@/captures/captures.utilities';
import { Root } from '@/core/Root';
import { sqLicenseManagementStore, sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { knowledgeManagementEnabled } from '@/knowledgeManagement/knowledgeManagement.availability';
import { LicenseManagementStoreProvider } from '@/licenseManagement/licenseManagement.store';
import { HeadlessCaptureStandby } from '@/main/HeadlessCaptureStandby';
import { LoadError } from '@/main/LoadError.page';
import { LocalisationWrapper } from '@/main/LocalisationWrapper';
import { Main } from '@/main/Main.page';
import { RouteError } from '@/main/RouteError.atom';
import { Unauthorized } from '@/main/Unauthorized.page';
import { Login } from '@/main/auth/Login.page';
import { ResetPassword } from '@/main/auth/ResetPassword.page';
import { page } from '@/main/routePage';
import { registerApiRequestTelemetry } from '@/main/telemetry.utilities';
import { Unsubscribe } from '@/notifications/Unsubscribe.page';
import { setupInterceptors } from '@/requests/axios.utilities';
import { canReadAuditTrail, canReadVersionLogs, isTrustedUser } from '@/services/authorization.service';
import { agentSkillsEnabled } from '@/services/systemConfiguration.utilities';
import { isHttpAbortError } from '@/utilities/http.utilities';
import { logError } from '@/utilities/logger';
import { formatMessage } from '@/utilities/logger.utilities';
import { initializeReduxDevtools } from '@/utilities/reduxDevtools.utilities';
import { initializeHeadlessCaptureMode } from '@/utilities/screenshot.utilities';
import { WorkbenchWrapperPage } from '@/workbench/WorkbenchWrapper.page';
import { APPSERVER_API_PREFIX, createAxiosInstance, setupApiSdk } from '@seeqdev/api';
import { QTip } from '@seeqdev/qomponents';
import type { AppRouteHandle } from '@/main/routePage';

// React.lazy only works via default exports so we import shim files which export a default component
// matching the given route
const Administration = React.lazy(() => import('@/defaultExportShims/Administration'));
const WorksheetWrapper = React.lazy(() => import('@/defaultExportShims/WorksheetWrapper'));
const HomeScreen = React.lazy(() => import('@/defaultExportShims/HomeScreen'));
const HomeScreenAddOnHost = React.lazy(() => import('@/defaultExportShims/HomeScreenAddOnHost'));
const ViewWrapper = React.lazy(() => import('@/defaultExportShims/ViewWrapper'));
const License = React.lazy(() => import('@/defaultExportShims/License'));
const LogTracker = React.lazy(() => import('@/defaultExportShims/LogTracker'));
const DataLabWrapper = React.lazy(() => import('@/defaultExportShims/DataLabWrapper'));
const AuditTrail = React.lazy(() => import('@/defaultExportShims/AuditTrail'));
const ReportTemplateThumbnail = React.lazy(() => import('@/defaultExportShims/ReportTemplateThumbnail'));
const NotificationsHistory = React.lazy(() => import('@/defaultExportShims/NotificationsHistory'));
const CapturesManagement = React.lazy(() => import('@/defaultExportShims/CapturesManagement'));
const NotificationsManagement = React.lazy(() => import('@/defaultExportShims/NotificationsManagement'));
const VantageManagement = React.lazy(() => import('@/defaultExportShims/VantageManagement'));
const DataLabManagement = React.lazy(() => import('@/defaultExportShims/DataLabManagement'));
const ExternalMLManagement = React.lazy(() => import('@/defaultExportShims/ExternalMLManagement'));

const AiAssistant = React.lazy(() => import('@/defaultExportShims/AiAssistant'));
const Usage = React.lazy(() => import('@/defaultExportShims/Usage'));
const ValueCapture = React.lazy(() => import('@/defaultExportShims/ValueCapture'));
const VersionLog = React.lazy(() => import('@/defaultExportShims/VersionLog'));
const MarkdownPreview = React.lazy(() => import('@/defaultExportShims/MarkdownPreview'));
const WorkspaceMarkdownCapture = React.lazy(() => import('@/defaultExportShims/WorkspaceMarkdownCapture'));
const AgentBuilder = React.lazy(() => import('@/defaultExportShims/AgentBuilder'));
const AgentBuilderDashboard = React.lazy(() => import('@/defaultExportShims/AgentBuilderDashboard'));
const SkillEditor = React.lazy(() => import('@/defaultExportShims/SkillEditor'));
const SkillsDashboard = React.lazy(() => import('@/defaultExportShims/SkillsDashboard'));
const KnowledgeManagement = React.lazy(() => import('@/defaultExportShims/KnowledgeManagement'));
const TreeBuilder = React.lazy(() => import('@/defaultExportShims/TreeBuilder'));
const TreeBuilderOverview = React.lazy(() => import('@/defaultExportShims/TreeBuilderOverview'));

initializeHeadlessCaptureMode();
initializeReduxDevtools();

// Also registers the instance globally; <Root>'s ApiProvider picks it up via getAxiosInstance()
const axiosInstance = createAxiosInstance();

setupInterceptors(axiosInstance, sqWorkbenchStore, sqWorkbookStore);
registerApiRequestTelemetry();
setupApiSdk({
  baseUrl: APPSERVER_API_PREFIX,
  axiosInstance,
  serializeParams: (params) => stringify(params, { indices: false }),
});

const worksheetRoutePaths = [
  'workbook/:workbookId/worksheet/:worksheetId',
  'workbook/:workbookId/worksheet',
  'workbook/:workbookId',
  ':currentFolderId/workbook/:workbookId/worksheet/:worksheetId',
  ':currentFolderId/workbook/:workbookId/worksheet',
  ':currentFolderId/workbook/:workbookId',
  'view/worksheet/:workbookId/:worksheetId',
  'view/worksheet/:workbookId',
  ':currentFolderId/view/worksheet/:workbookId/:worksheetId',
  ':currentFolderId/view/worksheet/:workbookId',
  'present/worksheet/:workbookId/:worksheetId',
  'present/worksheet/:workbookId',
  'present-plus/worksheet/:workbookId/:worksheetId',
  'present-plus/worksheet/:workbookId',
];

const agentSkillsAvailable = () => agentBuilderAvailable() && agentSkillsEnabled();

// Pages served under several paths share one page() result (or handle, when the
// element differs per path) so their telemetry routeName cannot drift apart.
const homeScreenPage = page(HomeScreen, { handle: { routeName: 'home-screen' }, permissions: 'login' });
const worksheetPage = page(WorksheetWrapper, { handle: { routeName: 'worksheet' }, permissions: 'login' });
const workspaceMarkdownCapturePage = page(WorkspaceMarkdownCapture, {
  handle: { routeName: 'workspace-markdown-capture' },
  permissions: 'login',
});
const agentBuilderPage = page(AgentBuilder, {
  handle: { routeName: 'agent-builder' },
  permissions: agentBuilderAvailable,
});
const agentBuilderDashboardPage = page(AgentBuilderDashboard, {
  handle: { routeName: 'agent-builder-dashboard' },
  permissions: agentBuilderAvailable,
});
const skillEditorPage = page(SkillEditor, { handle: { routeName: 'skill-editor' }, permissions: agentSkillsAvailable });
const treeBuilderPage = page(TreeBuilder, { handle: { routeName: 'tree-builder' }, permissions: 'login' });
const dataLabPage = page(DataLabWrapper, { handle: { routeName: 'data-lab' }, permissions: 'public' });
const unauthorizedPage = page(Unauthorized, { handle: { routeName: 'unauthorized' }, permissions: 'public' });
const loadErrorHandle: AppRouteHandle = { routeName: 'load-error' };

/**
 * Application is the de-facto Root component of the application where all the routes are configured
 */
export const Application = () => {
  useEffect(() => {
    window.onerror = function (errorMsg, url, lineNumber, colNumber, errObject) {
      if (errObject && errObject.stack) {
        logError(errObject);
      } else {
        logError(new Error(`${errorMsg} in ${url}, line: ${lineNumber}, column: ${colNumber}`));
      }
    };
    const rejectionHandle = (event: PromiseRejectionEvent) => {
      if (!isHttpAbortError(event.reason)) {
        logError(formatMessage`Unhandled exception: ${event.reason}`);
      }
      event.preventDefault();
    };
    window.addEventListener('unhandledrejection', rejectionHandle);
    return () => {
      window.removeEventListener('unhandledrejection', rejectionHandle);
    };
  }, []);

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route path="/" element={<Main />} errorElement={<RouteError />}>
          <Route path="/" element={<WorkbenchWrapperPage />}>
            <Route index {...homeScreenPage} />

            <Route path="ai" {...page(AiAssistant, { handle: { routeName: 'ai-assistant' }, permissions: 'login' })} />
            <Route
              path="md/*"
              {...page(MarkdownPreview, { handle: { routeName: 'markdown-preview' }, permissions: 'login' })}
            />
            <Route path="present/workspace-markdown/:chatId" {...workspaceMarkdownCapturePage} />
            <Route path="present/workspace-markdown-renderer/:chatId" {...workspaceMarkdownCapturePage} />
            <Route path="agent-builder" {...agentBuilderDashboardPage} />
            <Route path="agent-builder/drafts" {...agentBuilderDashboardPage} />
            <Route path="agent-builder/templates" {...agentBuilderDashboardPage} />
            <Route path="agent-builder/runs" {...agentBuilderDashboardPage} />
            <Route path="agent-builder/new" {...agentBuilderPage} />
            <Route path="agent-builder/drafts/:agentBuilderId" {...agentBuilderPage} />
            <Route path="agent-builder/templates/:agentBuilderId" {...agentBuilderPage} />
            <Route path="agent-builder/:agentBuilderId" {...agentBuilderPage} />
            {/* Skills Builder - integrated under Agent Builder, gated by AgentSkills feature flag */}
            <Route
              path="agent-builder/skills"
              {...page(SkillsDashboard, {
                handle: { routeName: 'skills-dashboard' },
                permissions: agentSkillsAvailable,
              })}
            />
            <Route path="agent-builder/skills/new" {...skillEditorPage} />
            <Route path="agent-builder/skills/:skillId" {...skillEditorPage} />

            <Route
              path="knowledge-management"
              {...page(KnowledgeManagement, {
                handle: { routeName: 'knowledge-management' },
                permissions: () => knowledgeManagementEnabled() && isTrustedUser(),
              })}
            />

            <Route path="workbooks" {...homeScreenPage} />

            <Route path=":currentFolderId/folder" {...homeScreenPage} />

            {_.map(worksheetRoutePaths, (path) => (
              <Route key={path} path={path} {...worksheetPage} />
            ))}

            <Route
              path="hsa"
              {...page(HomeScreenAddOnHost, {
                handle: { routeName: 'home-screen-add-on-host' },
                permissions: isTrustedUser,
              })}
            />

            <Route
              path="view/:viewId"
              {...page(ViewWrapper, { handle: { routeName: 'view' }, permissions: 'login' })}
            />

            <Route path="tree-builder">
              <Route
                index
                {...page(TreeBuilderOverview, { handle: { routeName: 'tree-builder-overview' }, permissions: 'login' })}
              />
              <Route path="new" {...treeBuilderPage} />
              <Route path=":tableId" {...treeBuilderPage} />
            </Route>

            <Route
              path="administration"
              {...page(Administration, { handle: { routeName: 'administration' }, permissions: 'login' })}>
              {ADMINISTRATION_ROUTE_PATHS.map((path) => (
                <Route key={path} path={path} />
              ))}
            </Route>

            <Route
              path="license"
              {...page(License, { handle: { routeName: 'license' }, permissions: isTrustedUser })}
            />

            <Route
              path="logs"
              {...page(LogTracker, { handle: { routeName: 'log-tracker' }, permissions: isTrustedUser })}
            />

            <Route path=":folderId/project/:projectId" {...dataLabPage} />
            <Route path="project/:projectId" {...dataLabPage} />

            <Route
              path="/impact"
              {...page(ValueCapture, { handle: { routeName: 'value-capture' }, permissions: isTrustedUser })}
            />

            <Route
              path="auditTrail"
              {...page(AuditTrail, { handle: { routeName: 'audit-trail' }, permissions: canReadAuditTrail })}
            />

            <Route
              path="version-log"
              {...page(VersionLog, { handle: { routeName: 'version-log' }, permissions: canReadVersionLogs })}
            />

            <Route
              path="captures"
              {...page(CapturesManagement, {
                handle: { routeName: 'captures' },
                permissions: () => shouldOfferCapturesManagement(isTrustedUser()),
              })}
            />

            <Route
              path="notifications-management"
              {...page(NotificationsManagement, {
                handle: { routeName: 'notifications-management' },
                permissions: isTrustedUser,
              })}
            />

            <Route
              path="vantage-management"
              {...page(VantageManagement, { handle: { routeName: 'vantage-management' }, permissions: isTrustedUser })}
            />

            <Route
              path="external-ml-management"
              {...page(ExternalMLManagement, {
                handle: { routeName: 'external-ml-management' },
                permissions: isTrustedUser,
              })}
            />

            <Route
              path="manage-data-lab"
              {...page(DataLabManagement, { handle: { routeName: 'data-lab-management' }, permissions: isTrustedUser })}
            />

            <Route path="my-usage" {...page(Usage, { handle: { routeName: 'usage' }, permissions: isTrustedUser })} />

            <Route
              path="notifications-history"
              {...page(NotificationsHistory, {
                handle: { routeName: 'notifications-history' },
                permissions: isTrustedUser,
              })}
            />
          </Route>
          <Route path="/" element={<LocalisationWrapper />}>
            <Route
              path="/headless-capture-standby"
              {...page(HeadlessCaptureStandby, {
                handle: { routeName: 'headless-capture-standby' },
                permissions: 'public',
              })}
            />

            <Route
              path="report-template/:templateId"
              {...page(ReportTemplateThumbnail, {
                handle: { routeName: 'report-template-thumbnail' },
                permissions: 'login',
              })}
            />

            <Route path="login" {...page(Login, { handle: { routeName: 'login' }, permissions: 'public' })} />

            <Route
              path="reset-password"
              {...page(ResetPassword, { handle: { routeName: 'reset-password' }, permissions: 'public' })}
            />

            <Route
              path="workbook/builder"
              {...page(Builder, { handle: { routeName: 'builder' }, permissions: 'login' })}
            />

            <Route path="unauthorized" {...unauthorizedPage} />

            <Route path="forbidden" {...unauthorizedPage} />

            <Route
              path="load-error"
              {...page(
                <LoadError
                  header="LOAD_ERROR.SERVER_HEADER"
                  message1="LOAD_ERROR.SERVER_MESSAGE1"
                  message2="LOAD_ERROR.SERVER_MESSAGE2"
                  retryInterval={3000}
                />,
                { handle: loadErrorHandle, permissions: 'public' },
              )}
            />

            <Route
              path="no-worksheet"
              {...page(
                <LoadError
                  header="LOAD_ERROR.NO_WORKSHEET_HEADER"
                  message1="LOAD_ERROR.NO_WORKSHEET_MESSAGE1"
                  message2="LOAD_ERROR.RETURN_TO_WORKBENCH"
                  retryInterval={0}
                />,
                { handle: loadErrorHandle, permissions: 'public' },
              )}
            />

            <Route
              path="no-workbook"
              {...page(
                <LoadError
                  header="LOAD_ERROR.NO_WORKBOOK_HEADER"
                  message1="LOAD_ERROR.NO_WORKBOOK_MESSAGE1"
                  message2="LOAD_ERROR.RETURN_TO_WORKBENCH"
                  retryInterval={0}
                />,
                { handle: loadErrorHandle, permissions: 'public' },
              )}
            />

            <Route
              path="unsubscribe/:notificationId"
              {...page(Unsubscribe, { handle: { routeName: 'unsubscribe' }, permissions: 'public' })}
            />

            <Route
              path="no-websocket"
              {...page(
                <LoadError
                  header="LOAD_ERROR.WEBSOCKET_HEADER"
                  message1="LOAD_ERROR.WEBSOCKET_MESSAGE1"
                  message2="LOAD_ERROR.WEBSOCKET_MESSAGE2"
                  retryInterval={0}
                />,
                { handle: loadErrorHandle, permissions: 'public' },
              )}
            />

            <Route
              path="connection-error"
              {...page(
                <LoadError
                  header="LOAD_ERROR.SERVER_HEADER"
                  message1="LOAD_ERROR.SERVER_MESSAGE1"
                  message2="LOAD_ERROR.SERVER_MESSAGE2"
                  retryInterval={3000}
                />,
                { handle: loadErrorHandle, permissions: 'public' },
              )}
            />
          </Route>
        </Route>
      </>,
    ),
  );

  return (
    <Root>
      <LicenseManagementStoreProvider store={sqLicenseManagementStore}>
        <QTip />
        <ToastContainer theme="colored" icon={false} />
        <RouterProvider router={router} />
      </LicenseManagementStoreProvider>
    </Root>
  );
};
